import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table } from 'app-components';
import { MM_SLASH_DD_SLASH_YYYY } from '@utils/dates/dateFormats.utils';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { EMPTY_VALUE } from '../../constants';
import { GenderCode, IEnrollee, IEnrolleesTableSectionProps } from '../../types';
import { getEnrolleeCount, safeFormatDate } from '../../utils';

// ============================================================================
// Constants
// ============================================================================

const ENROLLEE_COLUMNS = [
    {
        titleId: 'member.enrollment_list.premiumhistory.enrollee.type',
        key: 'type',
    },
    {
        titleId: 'member.enrollment_list.premiumhistory.enrollee.name',
        key: 'name',
    },
    {
        titleId: 'member.enrollment_list.premiumhistory.enrollee.gender',
        key: 'gender',
    },
    {
        titleId: 'label.dob',
        key: 'dateOfBirth',
        render: (data: IEnrollee) => (
            <span className="text-no-wrap">{data.dateOfBirth}</span>
        ),
    },
    {
        titleId: 'member.enrollment_list.premiumhistory.enrollee.tobacco',
        key: 'tobacco',
    },
    {
        titleId: 'member.enrollment_list.premiumhistory.enrollee.ssn',
        key: 'ssn',
    },
    {
        titleId: 'member.enrollment_list.premiumhistory.enrollee.coverageDates',
        key: 'coverageDates',
        render: (data: IEnrollee) => (
            <span className="text-no-wrap">{data.coverageDates}</span>
        ),
    },
    {
        titleId: 'historicalApp.member_ref_id',
        key: 'memberId',
    },
];

// ============================================================================
// Component
// ============================================================================

const EnrolleesTableSection: React.FC<IEnrolleesTableSectionProps> = ({ enrollment }) => {
    const intl = useIntl();
    const { enrolleeHistoryEntries } = enrollment;
    const naText = intl.formatMessage({ id: 'indportal.tobacco.table.data.na' });

    // Map API data to table format
    // Handle null/undefined with N/A, true with Yes, false with No
    const getTobaccoLabel = (value: string | boolean | null | undefined): string => {
        if (value == null) return naText;
        const boolValue = typeof value === 'string' ? value.toLowerCase() === 'true' : Boolean(value);
        return boolValue
            ? intl.formatMessage({ id: 'label.yes' })
            : intl.formatMessage({ id: 'label.no' });
    };

    const enrollees: IEnrollee[] = useMemo(() => {
        if (!enrolleeHistoryEntries?.length) return [];
        return enrolleeHistoryEntries.map(entry => {
            const formattedDob = safeFormatDate(entry.dob, MM_SLASH_DD_SLASH_YYYY);
            const formattedStartDate = safeFormatDate(entry.coverageStartDate, MM_SLASH_DD_SLASH_YYYY, '');
            const formattedEndDate = safeFormatDate(entry.coverageEndDate, MM_SLASH_DD_SLASH_YYYY, '');
            
            // Show EMPTY_VALUE if any date is missing, otherwise show "start - end" format
            const coverageDates = (formattedStartDate && formattedEndDate) 
                ? `${formattedStartDate} - ${formattedEndDate}`
                : EMPTY_VALUE;
            
            return {
                type: entry.relationship || EMPTY_VALUE,
                name: `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim() || EMPTY_VALUE,
                gender: entry.gender === GenderCode.MALE 
                    ? intl.formatMessage({ id: 'label.male' }) 
                    : entry.gender === GenderCode.FEMALE 
                        ? intl.formatMessage({ id: 'label.female' }) 
                        : EMPTY_VALUE,
                dateOfBirth: formattedDob,
                tobacco: getTobaccoLabel(entry.tobacco),
                ssn: socialSecurityNumberTools.maskSsnLastFour(entry.ssnLastFour) || EMPTY_VALUE,
                coverageDates,
                memberId: entry.memberId || EMPTY_VALUE,
            };
        });
    }, [enrolleeHistoryEntries, naText, intl]);

    // Calculate summary
    const enrolleeSummary = useMemo(() => {
        const counts = getEnrolleeCount(enrollees, e => e.type);
        return {
            primaryCount: counts.primary,
            spouseCount: counts.spouse,
            dependentCount: counts.dependent,
        };
    }, [enrollees]);

    return (
        <div>
            <h4 className="margin-top-0 margin-bottom-3">
                <FormattedMessage id="historical.member.applications.enrollees" values={enrolleeSummary} />
            </h4>
            <div className="historical-household-table">
                <Table
                    columns={ENROLLEE_COLUMNS}
                    data={enrollees}
                    classNamesStr="enrollees-table"
                />
            </div>
        </div>
    );
};

export default EnrolleesTableSection;
